import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk'
import { Suspense, useEffect, useState } from 'react'
import { SWRConfig, mutate } from 'swr'
import { useTranslation } from 'react-i18next'
import AppLoading from 'containers/App/AppLoading'
import AppTheme from 'containers/App/AppTheme'
import Routes from 'routes'
import 'i18n'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import 'ui/src/css/toastOverrides.css'
import globalStore from 'store'
import { install } from 'ga-gtag'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { enUS, fr } from 'date-fns/locale'

const swrCache = new Map()

const App = () => {
  const { i18n } = useTranslation()
  const ldClient = useLDClient()
  const user = globalStore(state => state.user)
  const register = globalStore(state => state.register)
  const getUser = globalStore(state => state.getUser)
  const loadingUser = globalStore(state => state.loadingUser)
  const loadingRegister = globalStore(state => state.loadingRegister)
  const getRegister = globalStore(state => state.getRegister)
  const [loading, setLoading] = useState(true)

  // Expose SWR's global mutate for interceptors
  useEffect(() => {
    window.SWRGlobalMutate = mutate
  }, [])

  useEffect(() => {
    if (ldClient) {
      const context = user
        ? {
            key: user.email,
            kind: 'user',
            id: register?.id,
            email: user.email,
            companyId: register?.company,
            cognitoId: register?.cognitoId,
          }
        : { key: 'anonymous', kind: 'anonymous', anonymous: true }
      ldClient.identify(context)
    }
  }, [user, register, ldClient, loadingUser])

  useEffect(() => {
    getUser()
  }, [])

  useEffect(() => {
    if (user) getRegister()
  }, [user])

  useEffect(() => {
    if (!loadingUser) {
      if (!user) {
        setLoading(false)
      } else if (!loadingRegister) {
        setLoading(false)
      }
    }
  }, [user, loadingUser, loadingRegister])

  install(process.env.REACT_APP_GTAG, { debug_mode: process.env.REACT_APP_GTAG_DEBUG_MODE })
  install(process.env.REACT_APP_GTAG_ADS, { debug_mode: process.env.REACT_APP_GTAG_DEBUG_MODE })

  if (loading) return <AppLoading />

  return (
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        dedupingInterval: 10000,
        focusThrottleInterval: 60000,
        shouldRetryOnError: false,
        errorRetryCount: 0,
        provider: () => swrCache,
      }}
    >
      <AppTheme>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={i18n.language?.toLowerCase().startsWith('fr') ? fr : enUS}
        >
          <ToastContainer
            enableMultiContainer
            containerId="system"
            position="top-center"
            autoClose={4000}
            hideProgressBar
            draggable={false}
            theme="light"
          />
          <Suspense fallback={<AppLoading />}>
            <Routes />
          </Suspense>
        </LocalizationProvider>
      </AppTheme>
    </SWRConfig>
  )
}

export default withLDProvider({
  clientSideID: process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID || '',
})(App)
